import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const LANG_KEY = "novifood_lang";
const CUR_KEY = "novifood_currency";
const RATES_KEY = "novifood_rates";

export const CURRENCIES = {
    EUR: { symbol: "€", rate: 1, locale: "pt-PT" },
    USD: { symbol: "$", rate: 1.08, locale: "en-US" },
    GBP: { symbol: "£", rate: 0.84, locale: "en-GB" },
};

const LocaleContext = createContext(null);

export function LocaleProvider({ children }) {
    const [lang, setLangState] = useState(() => localStorage.getItem(LANG_KEY) || "pt");
    const [currency, setCurrencyState] = useState(() => {
        const c = localStorage.getItem(CUR_KEY);
        return CURRENCIES[c] ? c : "EUR";
    });
    const [liveRates, setLiveRates] = useState(() => {
        try { return JSON.parse(localStorage.getItem(RATES_KEY))?.rates || null; } catch { return null; }
    });

    useEffect(() => {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        fetch(`${backendUrl}/api/exchange-rates`)
            .then((r) => r.json())
            .then((data) => {
                if (data?.rates?.USD) {
                    setLiveRates(data.rates);
                    localStorage.setItem(RATES_KEY, JSON.stringify(data));
                }
            })
            .catch(() => {});
    }, []);

    const setLang = useCallback((l) => {
        setLangState(l);
        localStorage.setItem(LANG_KEY, l);
        document.documentElement.lang = l === "en" ? "en" : "pt-PT";
    }, []);

    const setCurrency = useCallback((c) => {
        if (!CURRENCIES[c]) return;
        setCurrencyState(c);
        localStorage.setItem(CUR_KEY, c);
    }, []);

    const value = useMemo(() => {
        const en = lang === "en";
        const tr = (pt, enStr) => (en && enStr != null ? enStr : pt);
        const conf = CURRENCIES[currency];
        const rate = liveRates?.[currency] ?? conf.rate;
        const fmtPrice = (eur, { decimals = 0, approx = false } = {}) => {
            const v = eur * rate;
            const formatted = new Intl.NumberFormat(en ? "en-GB" : "pt-BR", {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
                useGrouping: true,
            }).format(v);
            return `${approx && currency !== "EUR" ? "≈" : ""}${conf.symbol} ${formatted}`;
        };
        return { lang, en, setLang, currency, setCurrency, tr, fmtPrice, currencySymbol: conf.symbol, rate };
    }, [lang, currency, liveRates, setLang, setCurrency]);

    return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
    const ctx = useContext(LocaleContext);
    if (!ctx) {
        return {
            lang: "pt", en: false, setLang: () => {}, currency: "EUR", setCurrency: () => {},
            tr: (pt) => pt, fmtPrice: (eur) => `€ ${new Intl.NumberFormat("pt-PT").format(eur)}`,
            currencySymbol: "€", rate: 1,
        };
    }
    return ctx;
}
